/**
 * voice-v8 — Image-Caption-Modal.
 *
 * Fragt vor dem Hochladen / Anhaengen eines Bildes eine Caption ab
 * ("Was soll ich mit diesem Bild tun?"). Lightweight Vanilla-DOM —
 * 2026-07-21: Diktat-Knopf (Whisper) + Live-Pegel, kein Auto-Fokus
 * am Touch, mobil Bottom-Sheet.
 * kein Framework, kein Pre-existierendes HTML noetig.
 *
 * Verhalten:
 *   - Backdrop + zentrierte Card (mobile-first, max 480px).
 *   - Bild-Preview oben (object-fit contain, max 240px hoch).
 *   - Auto-resize Textarea (2-6 Zeilen).
 *   - Quick-Suggestion-Chips fuellen das Textfeld.
 *   - Enter sendet, Shift+Enter macht Zeilenumbruch, Escape bricht ab.
 *   - Promise resolved bei Submit/Cancel — DOM + Listener werden cleaned-up.
 *
 * Stil: in Linie mit .sheet- / .lightbox- Komponenten (Backdrop-Blur,
 * ember-Buttons, hairline-Borders, Dark-Mode via CSS-Vars).
 */

import { createMicViz } from "./mic-visualizer.js";

import { wachsendesTextfeld } from "./sheet-ui.js";
/**
 * Pre.537: Eine Beleg-Kategorie als Schnell-Button im Beleg-Modus. Tippt der
 * User sie an, wird direkt auf das hinterlegte Konto verbucht (Bärli muss nur
 * noch Betrag/Vendor bestätigen).
 */
export interface ReceiptCategory {
  key: string;
  label: string;
  account_number: string;
  account_name: string;
  account_id: number;
}

export interface ImageCaptionResult {
  caption: string;
  cancelled: boolean;
  /** Pre.537: gewählte Beleg-Kategorie (nur im Beleg-Modus), sonst null. */
  category?: ReceiptCategory | null;
}

interface ImageCaptionOpts {
  previewUrl: string;
  filename?: string;
  defaultCaption?: string;
  /**
   * Pre.537: Beleg-Modus. Sind Kategorien gesetzt, zeigt die Modal statt der
   * generischen Bild-Chips ("Beschreib das Bild" …) Kategorie-Schnellbuttons
   * (bereits nach Häufigkeit sortiert vom Aufrufer) + das Textfeld nur noch
   * als „Zusatzinfo". Die gewählte Kategorie kommt im Result zurück.
   */
  receiptCategories?: ReceiptCategory[];
}

/**
 * Zeigt das Modal und resolved sobald der User Senden oder Abbrechen
 * (Button / Escape / Backdrop-Klick) ausloest.
 */
export function askImageCaption(
  opts: ImageCaptionOpts,
): Promise<ImageCaptionResult> {
  return new Promise<ImageCaptionResult>((resolve) => {
    const root = document.createElement("div");
    root.className = "image-caption-modal";
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("aria-label", "Bild-Caption");

    const backdrop = document.createElement("div");
    backdrop.className = "image-caption-backdrop";
    root.appendChild(backdrop);

    const card = document.createElement("div");
    card.className = "image-caption-card";
    root.appendChild(card);

    // --- Preview ---------------------------------------------------------
    const previewWrap = document.createElement("div");
    previewWrap.className = "image-caption-preview";
    const img = document.createElement("img");
    img.src = opts.previewUrl;
    img.alt = opts.filename ?? "Bild-Vorschau";
    img.className = "icm-preview-img";
    previewWrap.appendChild(img);
    // 2026-07-24 (Martin, Vorlage): kleines X am Thumbnail zum Verwerfen.
    const rmX = document.createElement("button");
    rmX.type = "button";
    rmX.className = "icm-preview-x";
    rmX.setAttribute("aria-label", "Bild verwerfen");
    rmX.title = "Bild verwerfen";
    rmX.textContent = "\u2715";
    rmX.addEventListener("click", () => onCancel());
    previewWrap.appendChild(rmX);
    card.appendChild(previewWrap);

    if (opts.filename) {
      const fn = document.createElement("p");
      fn.className = "icm-filename";
      fn.textContent = opts.filename;
      card.appendChild(fn);
    }

    const isReceipt =
      Array.isArray(opts.receiptCategories) &&
      opts.receiptCategories.length > 0;
    let selectedCategory: ReceiptCategory | null = null;

    // --- Textarea (Caption / im Beleg-Modus: Zusatzinfo) ----------------
    const label = document.createElement("label");
    label.className = "icm-label";
    label.textContent = isReceipt ? "Zusatzinfo (optional)" : "";

    const textarea = document.createElement("textarea");
    textarea.className = "icm-textarea";
    textarea.rows = 2;
    textarea.placeholder = isReceipt
      ? "z. B. Kunde, Anlass, Projekt …"
      : "Was soll ich damit tun? (optional)";
    // Im Beleg-Modus startet das Feld leer — der „verbuchen"-Auslöser läuft
    // unsichtbar im Aufrufer mit, der User tippt hier nur Zusatzinfos.
    textarea.value = isReceipt ? "" : (opts.defaultCaption ?? "");
    label.appendChild(textarea);

    // 2026-08-30: gerechnet aus den ECHTEN Werten des Feldes, nicht aus
    // festen Zahlen — die alte Rechnung (22 x 6 + 16 = 148) lag 4,5 Punkte
    // zu tief und schnitt die sechste Zeile an.
    const autoResize = wachsendesTextfeld(textarea, 6);

    // --- Diktat (2026-07-21, Martin: "fehlt auch hier der Mikrofon-Knopf") —
    // gleicher Whisper-Weg wie Composer/Frage-Sheet, gleicher Live-Pegel.
    // Start/Stopp NUR manuell; Stopp uebertraegt und haengt den Text an.
    const micRow = document.createElement("div");
    micRow.className = "icm-mic-row";
    const mic = document.createElement("button");
    mic.type = "button";
    mic.className = "icm-mic";
    mic.dataset.state = "idle";
    mic.setAttribute("aria-label", "Diktieren");
    mic.title = "Diktieren";
    const micNs = "http://www.w3.org/2000/svg";
    const micSvg = document.createElementNS(micNs, "svg");
    micSvg.setAttribute("viewBox", "0 0 24 24");
    micSvg.setAttribute("width", "20");
    micSvg.setAttribute("height", "20");
    micSvg.setAttribute("aria-hidden", "true");
    const micPath = document.createElementNS(micNs, "path");
    micPath.setAttribute("fill", "currentColor");
    micPath.setAttribute(
      "d",
      "M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z",
    );
    micSvg.appendChild(micPath);
    mic.appendChild(micSvg);
    const micViz = createMicViz();
    micRow.appendChild(mic);
    micRow.appendChild(micViz.el);
    // (micRow wandert in die Aktionsleiste unten)

    let micRec: MediaRecorder | null = null;
    let micStream: MediaStream | null = null;
    let micChunks: Blob[] = [];
    const micCleanup = (): void => {
      micViz.stop();
      micStream?.getTracks().forEach((t) => t.stop());
      micStream = null;
      micRec = null;
      mic.dataset.state = "idle";
    };
    const micStop = (commit: boolean): void => {
      const mr = micRec;
      if (!mr) return;
      micRec = null;
      mic.dataset.state = "idle";
      micViz.stop();
      mr.addEventListener("stop", () => {
        micStream?.getTracks().forEach((t) => t.stop());
        micStream = null;
        if (!commit || micChunks.length === 0) {
          micChunks = [];
          return;
        }
        const type = mr.mimeType || "audio/webm";
        const ext = /mp4|mpeg|m4a/.test(type) ? "mp4" : /ogg/.test(type) ? "ogg" : "webm";
        const fd = new FormData();
        fd.append("audio", new Blob(micChunks, { type }), `diktat.${ext}`);
        micChunks = [];
        const prevPh = textarea.placeholder;
        textarea.placeholder = "Wird übertragen …";
        fetch("/api/transcribe", { method: "POST", body: fd, credentials: "same-origin" })
          .then((r) => r.json())
          .then((j) => {
            const text = ((j as { text?: string } | null)?.text ?? "").trim();
            if (text) {
              const base = textarea.value ? textarea.value.replace(/\s*$/, "") + " " : "";
              textarea.value = (base + text).trimStart();
              autoResize();
            }
          })
          .catch(() => {
            /* Diktat ist Bonus — Tippen geht immer */
          })
          .finally(() => {
            textarea.placeholder = prevPh;
          });
      });
      try {
        mr.stop();
      } catch {
        micCleanup();
      }
    };
    mic.addEventListener("click", () => {
      if (micRec) {
        micStop(true);
        return;
      }
      void (async () => {
        try {
          micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          micChunks = [];
          const mr = new MediaRecorder(micStream);
          mr.addEventListener("dataavailable", (ev) => {
            if (ev.data && ev.data.size > 0) micChunks.push(ev.data);
          });
          micRec = mr;
          mr.start();
          mic.dataset.state = "recording";
          micViz.start(micStream);
        } catch {
          micCleanup();
        }
      })();
    });

    // --- Chips -----------------------------------------------------------
    // Beleg-Modus: Kategorie-Schnellbuttons (single-select, oben) + Zusatzinfo
    // darunter. Bild-Modus: generische Suggestions die das Textfeld füllen.
    const chips = document.createElement("div");
    chips.className = "icm-chips";
    const chipButtons: HTMLButtonElement[] = [];

    if (isReceipt) {
      const catLabel = document.createElement("div");
      catLabel.className = "icm-label";
      catLabel.textContent = "Kategorie (tippen = direkt verbuchen)";
      card.appendChild(catLabel);
      for (const cat of opts.receiptCategories ?? []) {
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "icm-chip icm-chip--cat";
        chip.textContent = cat.label;
        // „tippen = direkt verbuchen": Chip-Tap wählt das Konto UND schließt
        // die Modal sofort — kein extra „Senden". Eine evtl. getippte Zusatz-
        // info wird mitgenommen.
        chip.addEventListener("click", () => {
          selectedCategory = cat;
          chip.classList.add("icm-chip--active");
          onSubmit();
        });
        chips.appendChild(chip);
        chipButtons.push(chip);
      }
      card.appendChild(chips);
      card.appendChild(label); // Zusatzinfo-Feld UNTER den Kategorien
    } else {
      // 2026-07-24 (Martin, Claude-App-Vorlage): Bild-Modus OHNE Vorschlags-
      // Chips — er schreibt eh immer selbst. Nur das grosszuegige Textfeld.
      card.appendChild(label);
    }

    // --- Aktionsleiste (2026-07-24, Martin, Claude-App-Vorlage): rundes X
    // links, Mikro/Wellenform in der Mitte, runder Senden-Knopf rechts.
    const actions = document.createElement("div");
    actions.className = "icm-actions";

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "icm-btn icm-btn--round icm-btn--cancel";
    cancelBtn.setAttribute("aria-label", "Abbrechen");
    cancelBtn.title = "Abbrechen";
    cancelBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">' +
      '<path fill="currentColor" d="M18.3 5.71 12 12.01 5.7 5.7 4.29 7.12l6.3 6.3' +
      '-6.3 6.29 1.41 1.41 6.3-6.3 6.29 6.3 1.41-1.41-6.3-6.29 6.3-6.3z"/></svg>';
    actions.appendChild(cancelBtn);

    // Mikro + Wellenform in der Mitte.
    actions.appendChild(micRow);

    const sendBtn = document.createElement("button");
    sendBtn.type = "button";
    sendBtn.className = "icm-btn icm-btn--round icm-btn--send";
    sendBtn.setAttribute("aria-label", "Senden");
    sendBtn.title = "Senden";
    sendBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">' +
      '<path fill="currentColor" d="M12 4 4 12l1.41 1.41L11 7.83V20h2V7.83l5.59 5.58L20 12z"/></svg>';
    actions.appendChild(sendBtn);

    card.appendChild(actions);

    // --- Cleanup + Resolve ----------------------------------------------
    let settled = false;
    const finish = (result: ImageCaptionResult): void => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKeyDown, true);
      backdrop.removeEventListener("click", onCancel);
      cancelBtn.removeEventListener("click", onCancel);
      sendBtn.removeEventListener("click", onSubmit);
      textarea.removeEventListener("keydown", onTextareaKey);
      textarea.removeEventListener("input", autoResize);
      for (const chip of chipButtons) {
        chip.replaceWith(chip.cloneNode(true)); // killt alle Listener
      }
      try {
        micRec?.stop();
      } catch {
        /* schon zu */
      }
      micCleanup();
      if (root.parentNode) root.parentNode.removeChild(root);
      resolve(result);
    };

    const onSubmit = (): void => {
      finish({
        caption: textarea.value.trim(),
        cancelled: false,
        category: selectedCategory,
      });
    };

    const onCancel = (): void => {
      finish({ caption: "", cancelled: true, category: null });
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    };

    const onTextareaKey = (e: KeyboardEvent): void => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSubmit();
      }
    };

    backdrop.addEventListener("click", onCancel);
    cancelBtn.addEventListener("click", onCancel);
    sendBtn.addEventListener("click", onSubmit);
    textarea.addEventListener("keydown", onTextareaKey);
    document.addEventListener("keydown", onKeyDown, true);

    // --- Mount + Focus ---------------------------------------------------
    document.body.appendChild(root);
    // 2026-07-21 (Martin): am Touch KEIN Auto-Fokus — die Tastatur
    // verdeckte das halbe Sheet; wer tippen will, tippt ins Feld.
    requestAnimationFrame(() => {
      autoResize();
      if (window.matchMedia("(pointer: fine)").matches) {
        textarea.focus();
        const len = textarea.value.length;
        textarea.setSelectionRange(len, len);
      }
    });
  });
}
